import React, { useEffect, useRef, useState } from "react";
import { Globe, Check, Loader2 } from "lucide-react";
import { api } from "../lib/api";

const LANGS = [["en", "English"], ["es", "Español"], ["fr", "Français"], ["pt", "Português"], ["it", "Italiano"], ["de", "Deutsch"], ["hi", "हिन्दी"],
  ["zh", "中文"], ["ar", "العربية"], ["tl", "Tagalog"], ["vi", "Tiếng Việt"], ["ko", "한국어"], ["ja", "日本語"]];
const orig = new WeakMap();
const mine = new WeakMap();
const SKIP = "script,style,noscript,textarea,code,[data-no-translate]";
const cacheOf = (l) => { try { return JSON.parse(localStorage.getItem(`lif_tr_${l}`) || "{}"); } catch { return {}; } };

function textNodes(root) {
  const out = [];
  const w = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, { acceptNode: (n) => (n.parentElement?.closest(SKIP) || !/\p{L}/u.test(n.nodeValue) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT) });
  while (w.nextNode()) out.push(w.currentNode);
  return out;
}

// Translates every visible text node and placeholder under #root, re-running as React re-renders.
function useAutoTranslate(lang, setBusy) {
  const cache = useRef({});
  useEffect(() => {
    const root = document.getElementById("root");
    document.documentElement.lang = lang;
    let timer = null, alive = true;
    cache.current = cacheOf(lang);
    const apply = async () => {
      const nodes = textNodes(root), missing = new Set();
      const phs = [...root.querySelectorAll("[placeholder]")].filter((e) => !e.closest(SKIP));
      const src = (n) => { if (mine.get(n) !== n.nodeValue) orig.set(n, n.nodeValue); return orig.get(n) ?? n.nodeValue; };
      const setTo = (n, v) => { if (n.nodeValue !== v) n.nodeValue = v; mine.set(n, v); };
      for (const n of nodes) {
        const o = src(n), t = o.trim();
        if (lang === "en") { setTo(n, o); continue; }
        const tr = cache.current[t];
        if (tr) setTo(n, o.replace(t, tr)); else missing.add(t);
      }
      for (const e of phs) {
        if (!e.dataset.origPh || e.getAttribute("placeholder") !== e.dataset.trPh) e.dataset.origPh = e.getAttribute("placeholder");
        const t = e.dataset.origPh.trim();
        const v = lang === "en" ? e.dataset.origPh : cache.current[t];
        if (v) { e.setAttribute("placeholder", v); e.dataset.trPh = v; } else if (t) missing.add(t);
      }
      if (lang === "en" || !missing.size) return;
      setBusy(true);
      try {
        const { data } = await api.post("/public/translate", { lang, texts: [...missing].slice(0, 200) });
        if (!alive) return;
        Object.assign(cache.current, data.translations);
        localStorage.setItem(`lif_tr_${lang}`, JSON.stringify(cache.current));
        if (Object.keys(data.translations).length) schedule();
      } catch { /* keep the original text */ } finally { setBusy(false); }
    };
    const schedule = () => { clearTimeout(timer); timer = setTimeout(apply, 250); };
    const mo = new MutationObserver(schedule);
    mo.observe(root, { childList: true, subtree: true, characterData: true });
    apply();
    return () => { alive = false; mo.disconnect(); clearTimeout(timer); };
  }, [lang, setBusy]);
}

export function LanguagePicker({ className = "fixed top-3 left-3", up = false }) {
  const [lang, setLang] = useState(() => new URLSearchParams(window.location.search).get("lang") || localStorage.getItem("lif_lang") || "en");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  useAutoTranslate(lang, setBusy);
  const pick = (c) => { localStorage.setItem("lif_lang", c); setLang(c); setOpen(false); };
  return (
    <div className={`${className} z-[90]`} data-no-translate data-testid="lang-picker">
      <button onClick={() => setOpen(!open)} className="flex items-center gap-1.5 bg-black/60 backdrop-blur text-white rounded-full pl-2.5 pr-3 py-1.5 text-xs shadow-lg hover:bg-black/75 transition-colors" aria-label="Choose language" data-testid="lang-picker-btn">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Globe className="w-3.5 h-3.5" />} {LANGS.find(([c]) => c === lang)?.[1]}
      </button>
      {open && (
        <div className={`${up ? "absolute bottom-full mb-2" : "mt-2"} w-44 max-h-80 overflow-y-auto bg-white rounded-xl shadow-2xl border border-black/5 py-1 text-sm text-[#111]`} data-testid="lang-menu">
          {LANGS.map(([c, n]) => <button key={c} onClick={() => pick(c)} className="w-full flex items-center justify-between px-3 py-1.5 hover:bg-black/5" data-testid={`lang-option-${c}`}>{n}{c === lang && <Check className="w-3.5 h-3.5 text-[var(--gold)]" />}</button>)}
          <p className="px-3 pt-1 pb-1.5 text-[10px] text-black/40">Translated by AI</p>
        </div>
      )}
    </div>
  );
}
